/**
 * Enhanced document highlighting utilities for LexaLabs
 * 
 * This module implements improved fuzzy matching for highlighting quotes
 * in documents with better handling of minor text differences.
 */

import { escapeRegExp } from './fileUtils';
import { logInfo, logDebug, logWarn, logHighlightOperation } from './debugLogger';

/**
 * Normalize text for fuzzy comparison
 */
const normalizeText = (text: string): string => {
  if (!text) return '';
  
  // Normalize whitespace
  let normalized = text.trim().replace(/\s+/g, ' ');
  
  // Normalize quotes
  normalized = normalized.replace(/[""]/g, '"').replace(/['']/g, "'");
  
  // Normalize dashes
  normalized = normalized.replace(/[–—−]/g, '-');
  
  return normalized;
};

/**
 * Check if HTML already has highlights from backend
 */
const hasBackendHighlights = (html: string): boolean => {
  const highlightCount = (html.match(/class="highlighted-text/g) || []).length;
  const debugInfoPattern = /<!--\s*Highlighting Debug Info:/;
  
  return highlightCount > 0 || debugInfoPattern.test(html);
};

/**
 * Find phrases that might be easier to match
 */
const extractKeyPhrases = (text: string, minWords = 3, minLength = 20, maxLength = 100): string[] => {
  const phrases = text.split(/[.!?;:,]/)
    .map(p => p.trim())
    .filter(p => {
      const wordCount = p.split(/\s+/).length;
      return wordCount >= minWords && p.length >= minLength && p.length <= maxLength;
    });
  
  return phrases;
};

/**
 * Enhanced highlight function with improved fuzzy matching capabilities
 */
export const enhancedHighlightHtml = (
  htmlContent: string, 
  textToHighlight: string[], 
  options = { minSimilarity: 0.7 }
): string => {
  if (!textToHighlight || textToHighlight.length === 0 || !htmlContent) {
    logWarn("Highlighting skipped - missing content or quotes");
    return htmlContent;
  }

  logInfo(`Starting enhanced highlighting operation`, {
    contentLength: htmlContent.length,
    quotesCount: textToHighlight.length
  });
  
  // Check if the HTML already contains highlights from the backend
  if (hasBackendHighlights(htmlContent)) {
    logInfo(`HTML already contains highlights from backend - skipping client-side highlighting`);
    
    // Add our own debug info
    const clientDebugInfo = `
    <!--
    Client highlighting info:
    - Backend highlights detected
    - Client-side highlighting skipped
    - Quotes provided to client: ${textToHighlight.length}
    -->`;
    
    // Add the debug info before the closing body tag
    return htmlContent.replace('</body>', `${clientDebugInfo}\n</body>`);
  }

  // If no backend highlights, proceed with client-side highlighting
  let processedHtml = htmlContent;
  const successfulMatches: string[] = [];
  const successfulReplacements: number[] = [];
  
  // Extract plain text from HTML for improved matching
  const plainTextContent = processedHtml.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');

  // Step 1: Exact matching pass
  textToHighlight.forEach((text, index) => {
    if (!text || text.trim() === '') {
      logWarn(`Empty quote at index ${index}, skipping`);
      return;
    }

    // Clean and prepare the text for regex
    const cleanText = text.trim();
    const escapedText = escapeRegExp(cleanText);

    logDebug(`Processing quote ${index}`, {
      quoteLength: cleanText.length,
      quoteSample: cleanText.substring(0, 50) + (cleanText.length > 50 ? '...' : '')
    });

    // Use regex to check for exact matches
    const regex = new RegExp(escapedText, 'g');
    const hasMatches = regex.test(processedHtml);
    regex.lastIndex = 0; // Reset regex index

    if (hasMatches) {
      // Create a replacement with highlighting
      const replacement = `<span class="highlighted-text" data-highlight-id="${index}" data-match-type="exact" style="background-color: #FFFF00; display: inline;">${cleanText}</span>`;
      processedHtml = processedHtml.replace(regex, replacement);
      successfulMatches.push(cleanText);
      successfulReplacements.push(index);
      logDebug(`Successfully highlighted exact match for quote ${index}`);
    } else {
      logWarn(`No exact match found for quote ${index}`);
    }
  });

  // Step 2: Flexible matching for unmatched quotes
  const unmatchedQuotes = textToHighlight.filter((_, index) => 
    !successfulReplacements.includes(index) && textToHighlight[index] && textToHighlight[index].trim() !== '');
  
  if (unmatchedQuotes.length > 0) {
    logInfo(`Attempting flexible matching for ${unmatchedQuotes.length} unmatched quotes`);
    
    unmatchedQuotes.forEach((text, idx) => {
      const originalIndex = textToHighlight.indexOf(text);
      const cleanText = text.trim();
      
      // Try sentence-by-sentence approach
      const sentences = cleanText.split(/(?<=[.!?])\s+/).filter(s => s.trim().length >= 15);
      
      if (sentences.length > 0) {
        logDebug(`Split quote ${originalIndex} into ${sentences.length} sentences`);
        
        let sentenceMatched = false;
        
        // Try to match each sentence
        sentences.forEach((sentence, sentIdx) => {
          if (sentenceMatched) return; // Skip if we already matched something from this quote
          
          const sentenceClean = sentence.trim();
          const escapedSentence = escapeRegExp(sentenceClean);
          
          // Create flexible pattern for fuzzy matching
          const flexiblePattern = escapedSentence
            .replace(/\s+/g, '\\s*') // Make whitespace flexible
            .replace(/([.!?,;:])/g, '\\s*$1\\s*') // Allow whitespace around punctuation
            .replace(/'/g, "[''']") // Handle quote variations
            .replace(/"/g, '["""]'); // Handle double quote variations
          
          const regex = new RegExp(flexiblePattern, 'gi');
          const hasMatches = regex.test(processedHtml);
          regex.lastIndex = 0;
          
          if (hasMatches) {
            const replacement = `<span class="highlighted-text" data-highlight-id="${originalIndex}-${sentIdx}" data-match-type="fuzzy" style="background-color: #FFFF00; display: inline;">$&</span>`;
            processedHtml = processedHtml.replace(regex, replacement);
            sentenceMatched = true;
            logDebug(`Successfully matched sentence ${sentIdx} from quote ${originalIndex} using fuzzy matching`);
          }
        });
        
        // If no sentence matched, try key phrases
        if (!sentenceMatched) {
          const keyPhrases = extractKeyPhrases(cleanText);
          
          if (keyPhrases.length > 0) {
            logDebug(`Attempting key phrase matching with ${keyPhrases.length} phrases for quote ${originalIndex}`);
            
            for (const phrase of keyPhrases) {
              if (phrase.length < 20) continue; // Skip phrases that are too short
              
              const normalizedPhrase = normalizeText(phrase);
              const escapedPhrase = escapeRegExp(normalizedPhrase);
              
              // Look for this phrase in the HTML content
              const phrasePattern = escapedPhrase
                .replace(/\s+/g, '\\s+')
                .replace(/'/g, "[''']")
                .replace(/"/g, '["""]');
              
              const phraseRegex = new RegExp(phrasePattern, 'gi');
              
              if (phraseRegex.test(processedHtml)) {
                phraseRegex.lastIndex = 0;
                const phraseReplacement = `<span class="highlighted-text" data-highlight-id="${originalIndex}-phrase" data-match-type="phrase" style="background-color: #FFFF00; display: inline;">$&</span>`;
                processedHtml = processedHtml.replace(phraseRegex, phraseReplacement);
                sentenceMatched = true;
                logDebug(`Matched key phrase from quote ${originalIndex}`);
                break;
              }
            }
          }
        }
        
        // If still no match, try word-based approach for important terms
        if (!sentenceMatched) {
          // Extract important words (nouns, proper names, etc.) that are 6+ chars
          const importantWords = cleanText.split(/\s+/)
            .filter(word => word.length >= 6 && /^[A-Z]/.test(word)); // Capitalized words 6+ chars
          
          if (importantWords.length >= 3) {
            logDebug(`Attempting important term matching with ${importantWords.length} terms`);
            
            // Look for paragraphs containing at least 3 of these terms
            const termPattern = importantWords
              .map(term => `(?=.*${escapeRegExp(term)})`)
              .join('');
            
            // This regex uses lookaheads to find text containing all terms
            const termsRegex = new RegExp(`${termPattern}.{50,500}`, 'gi');
            
            if (termsRegex.test(plainTextContent)) {
              // If we find a section with these terms, highlight it
              const match = termsRegex.exec(plainTextContent);
              if (match && match[0]) {
                const matchText = match[0].trim();
                const escapedMatch = escapeRegExp(matchText);
                
                // Only try to highlight if this text appears in the HTML
                if (new RegExp(escapedMatch, 'g').test(processedHtml)) {
                  try {
                    const termReplacement = `<span class="highlighted-text" data-highlight-id="${originalIndex}-terms" data-match-type="terms" style="background-color: #FFFF00; display: inline;">${matchText}</span>`;
                    processedHtml = processedHtml.replace(new RegExp(escapedMatch, 'g'), termReplacement);
                    logDebug(`Matched by important terms from quote ${originalIndex}`);
                  } catch (e) {
                    logWarn(`Error applying terms highlight: ${e}`);
                  }
                }
              }
            }
          }
        }
      }
    });
  }

  // Count successful highlights
  const highlightCount = (processedHtml.match(/background-color: #FFFF00/g) || []).length;
  
  // Log overall statistics
  logHighlightOperation(
    "document", 
    htmlContent.length, 
    textToHighlight, 
    highlightCount
  );

  // Add a debug info comment at the end of the HTML
  const debugInfo = `
  <!--
  Enhanced client highlighting info:
  - Total quotes attempted: ${textToHighlight.length}
  - Exact matches found: ${successfulMatches.length}
  - Total highlights applied: ${highlightCount}
  - Highlighting algorithm: Enhanced fuzzy matching
  -->`;

  // Add the debug info before the closing body tag
  processedHtml = processedHtml.replace('</body>', `${debugInfo}\n</body>`);

  return processedHtml;
};